import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import FormItem from 'antd/es/form/FormItem';
import { DefaultOptionType } from 'antd/es/select';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { CustomerApis } from 'apis/CustomerApis';
import { PlantApi } from 'apis/PlantApis';
import { PurChaseInvoiceApi } from 'apis/PurchaseInvoiceApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { FormFilter, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { IPlant } from 'interfaces/Plant/Plant';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import {
  ApiResponse,
  ComboBoxOption,
} from 'pages/Product/ProductSloc/FilterProductSloc';

export interface IPurchaseInvoiceFilterForm {
  id?: string;
  poId?: string;
  customerId?: string;
  date?: any[];
  fromDate?: string;
  toDate?: string;
  purchaseType?: string;
  purchaseStatus?: string;
  childPurchaseOrderId?: string;
  plantId?: string;
  teamSalesId?: string;
}

interface IProps {
  isOpen: boolean;
  defaultFilter: any;
  onCancel: () => void;
  onFinish(values: IPurchaseInvoiceFilterForm): void;
}

const { RangePicker } = DatePicker;

const FilterPurchaseInvoice = forwardRef(
  ({ isOpen, defaultFilter, onCancel, onFinish }: IProps, ref) => {
    const { user } = useUser();
    const [form] = useForm<IPurchaseInvoiceFilterForm>();

    const [plants, setPlants] = useState<IPlant[]>([]);
    const [teamData, setTeamData] = useState(initLazySelectData);
    const [typePurchase, setTypePurchase] = useState<ComboBoxOption[]>([]);
    const [purchaseStatus, setPurchaseStatus] = useState<DefaultOptionType[]>(
      [],
    );

    const [customersData, setCustomersData] =
      useState<ILazySelectData>(initLazySelectData);

    const { mutate: getAllPlant, isPending: isGetPlantsPending } = useMutation({
      mutationFn: () => PlantApi.getAllPlantComboBox(),
      onSuccess: (res: any) => setPlants(res.data?.getPlant?.items || []),
      onError: (error) => console.log(error),
    });

    const { mutate: getTeamSaleCbb, isPending: isGetTeamSaleCbbPending } =
      useMutation({
        mutationFn: (params: IFilterTeamSaleCb) =>
          SaleTeamApi.getAllTeamSaleCb(params),
        onSuccess: (res) => {
          const items = res?.data?.getTeamSales?.items;
          const totalItems = res?.data?.getTeamSales?.pageInfo?.totalItems;

          setTeamData({
            totalItems,
            options: items?.map((item: any) => ({
              label: `${item?.subId} - ${item?.name}`,
              value: item?.id,
            })),
          });
        },
        onError: (error) => {
          console.log(error);
        },
      });

    const { mutate: getTypePurchaseInvoice } = useMutation({
      mutationFn: () => PurChaseInvoiceApi.getTypePurchase(),
      onSuccess: (res: ApiResponse<{ id: string; name: string }>) =>
        setTypePurchase(
          res?.data?.getCode?.items
            ? res?.data?.getCode?.items?.map((item) => ({
                value: item?.id,
                label: `${item?.id} - ${item?.name}`,
              }))
            : [],
        ),
    });

    const { mutate: getPurchaseStatusCbx } = useMutation({
      mutationFn: () => PurChaseInvoiceApi.getPurchaseStatusCbx(),
      onSuccess: (res: ApiResponse<{ id: string; name: string }>) =>
        setPurchaseStatus(
          res?.data?.getCode?.items
            ? res?.data?.getCode?.items?.map((item) => ({
                value: item?.id,
                label: item?.name,
              }))
            : [],
        ),
    });

    const {
      mutate: getCustomersMutate,
      isPending: isGetCustomersMutatePending,
    } = useMutation({
      mutationFn: (params: { pageSize: number; searchValue: string }) =>
        CustomerApis.getCustomersInCombobox(params),
      onSuccess: (res) => {
        if (res?.data?.getCustomer) {
          const data = res?.data?.getCustomer;

          setCustomersData({
            options: data?.items?.map(
              (item: any) =>
                ({
                  label:
                    item?.id + (item?.storeName ? ` - ${item?.storeName}` : ''),
                  value: item?.id,
                }) as DefaultOptionType,
            ),
            totalItems: data?.pageInfo?.totalItems,
          });
        }
      },
    });

    useEffect(() => {
      getAllPlant();
      getPurchaseStatusCbx();
      getTypePurchaseInvoice();
      getTeamSaleCbb({ pageSize: 10 });
    }, []);

    useEffect(() => {
      if (!isOpen) return;

      form.setFieldsValue({
        ...defaultFilter,
      });
    }, [isOpen, defaultFilter]);

    const handleSetFormValues = useCallback(
      (values: IPurchaseInvoiceFilterForm) => {
        form.setFieldsValue(values);
      },
      [form],
    );

    const checkFormValidate = useCallback(async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    }, [form]);

    const handleCancel = useCallback(() => {
      form.resetFields();
      form.setFieldsValue({
        ...defaultFilter,
      });

      onCancel();
    }, [defaultFilter]);

    const handleFilter = useCallback(
      async (values: IPurchaseInvoiceFilterForm) => {
        if (!(await checkFormValidate())) return;

        const payload: IPurchaseInvoiceFilterForm = {
          ...values,
          fromDate: values.date?.[0],
          toDate: values.date?.[1],
        };
        onFinish(payload);
      },
      [],
    );

    const handleTeamSelectScrollChange = (values: ILazySelectParams) => {
      const params: IFilterTeamSaleCb = {
        ...values,
      };
      getTeamSaleCbb(params);
    };

    const handleScrollCustomersChange = (params: ILazySelectParams) => {
      const { pageSize, value } = params;

      getCustomersMutate({
        pageSize,
        searchValue: value || '',
      });
    };

    useImperativeHandle(ref, () => ({ handleSetFormValues }));

    return (
      <Content isOpen={isOpen}>
        <FormFilter form={form} onFinish={handleFilter} onCancel={handleCancel}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <FormItem name="id" label="Mã đơn dẻ">
                <InputForm name="id" placeholder="Tìm mã đơn dẻ" />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem name="poId" label="Mã đơn PO">
                <InputForm name="poId" placeholder="Tìm mã đơn PO" />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem name="customerId" label="Mã KH">
                <LazySelectForm
                  placeholder="Tìm MST/Mã khách hàng"
                  data={customersData}
                  loading={isGetCustomersMutatePending}
                  onSelectScrollChange={handleScrollCustomersChange}
                />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem name="date" label="Ngày tạo từ - đến">
                <RangePicker
                  name="date"
                  format="DD/MM/YYYY"
                  className="w-full h-10"
                  placeholder={['Từ ngày', 'Đến ngày']}
                />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem name="purchaseType" label="Loại thu dẻ">
                <SelectForm
                  placeholder="Chọn Loại thu dẻ"
                  options={typePurchase}
                />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem name="purchaseStatus" label="Trạng thái">
                <SelectForm
                  placeholder="Chọn trạng thái"
                  options={purchaseStatus}
                />
              </FormItem>
            </Col>
            {/* <Col span={6}>
            <FormItem name="childPurchaseOrderId" label="Đơn tách">
              <SelectForm placeholder="Chọn đơn tách" />
            </FormItem>
          </Col> */}
            <Col span={6}>
              <FormItem label="Chi nhánh" name="plantId">
                <SelectForm
                  placeholder="Chọn chi nhánh"
                  loading={isGetPlantsPending}
                  disabled={
                    user?.isAccountant || user?.isTreasurer || user?.isKTD
                  }
                  options={plants.map((plant) => ({
                    label: plant?.id,
                    value: plant?.id,
                  }))}
                />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem label="Đội bán hàng" name="teamSalesId">
                <LazySelectForm
                  data={teamData}
                  placeholder="Chọn đội"
                  loading={isGetTeamSaleCbbPending}
                  onSelectScrollChange={handleTeamSelectScrollChange}
                />
              </FormItem>
            </Col>
          </Row>
        </FormFilter>
      </Content>
    );
  },
);

export default memo(FilterPurchaseInvoice);
